<template>
  <div class="pg-mem-notes">
    <div class="pg-mem-notes__header">
      <h6 class="pg-mem-notes__title">
        <i class="material-icons-round">edit_note</i>
        Memory Interpretation Notes Game
      </h6>
    </div>

    <!-- Note Chunks Section -->
    <div class="pg-mem-notes__section">
      <label class="pg-mem-notes__section-label">
        <i class="material-icons-round">sticky_note_2</i>
        Note Chunks
      </label>

      <div class="pg-mem-notes__list">
        <div
          v-for="(chunk, index) in sectionData.content.chunks"
          :key="index"
          class="pg-mem-notes__item"
        >
          <div class="pg-mem-notes__item-head">
            <span class="pg-mem-notes__index">{{ index + 1 }}</span>
            <div class="pg-mem-notes__item-actions">
              <button
                type="button"
                class="pg-mem-notes__icon-btn pg-mem-notes__icon-btn--danger"
                title="Delete chunk"
                @click="removeChunk(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>

          <div class="pg-mem-notes__item-body">
            <!-- Source Content Section -->
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-mem-notes__field-ico">description</i>
                Source Content
              </label>
              <textarea
                v-model="chunk.sourceContent"
                class="p-input p-input--area"
                rows="4"
                placeholder="메모할 원본 내용을 입력하세요..."
              />
            </div>

            <!-- Key Symbols Section -->
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-mem-notes__field-ico">code</i>
                Key Symbols/Abbreviations
              </label>
              <textarea
                v-model="chunk.keySymbols"
                class="p-input p-input--area"
                rows="3"
                placeholder="사용할 기호나 축약어를 입력하세요 (한 줄에 하나씩)..."
              />
            </div>

            <!-- Expected Notes Section -->
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-mem-notes__field-ico">check_box</i>
                Expected Notes
              </label>
              <textarea
                v-model="chunk.expectedNotes"
                class="p-input p-input--area"
                rows="3"
                placeholder="예상 메모 결과를 입력하세요..."
              />
            </div>

            <!-- Settings Section -->
            <div class="p-form-grid p-form-grid--3 pg-mem-notes__settings">
              <div class="p-field">
                <label class="p-field__label">
                  <i class="material-icons-round pg-mem-notes__field-ico">schedule</i>
                  Time Limit (seconds)
                </label>
                <input
                  type="number"
                  v-model.number="chunk.timeLimit"
                  class="p-input"
                  min="30"
                  max="300"
                  placeholder="메모 시간"
                />
              </div>
              <div class="p-field">
                <label class="p-field__label">
                  <i class="material-icons-round pg-mem-notes__field-ico">signal_cellular_alt</i>
                  Difficulty
                </label>
                <select v-model="chunk.difficulty" class="p-input">
                  <option value="beginner">초급</option>
                  <option value="intermediate">중급</option>
                  <option value="advanced">고급</option>
                </select>
              </div>
              <div class="p-field">
                <label class="p-field__label">
                  <i class="material-icons-round pg-mem-notes__field-ico">speed</i>
                  Speech Rate
                </label>
                <select v-model="chunk.speechRate" class="p-input">
                  <option value="slow">느림</option>
                  <option value="normal">보통</option>
                  <option value="fast">빠름</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="pg-mem-notes__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addChunk">Add Note Chunk</ABtn>
        </div>
      </div>
    </div>

    <!-- Assessment Criteria -->
    <div class="pg-mem-notes__section">
      <label class="pg-mem-notes__section-label">
        <i class="material-icons-round">bar_chart</i>
        Assessment Criteria
      </label>
      <div class="pg-mem-notes__assessment">
        <div class="p-form-grid p-form-grid--2">
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round pg-mem-notes__field-ico">gps_fixed</i>
              Accuracy Weight (%)
            </label>
            <input
              type="number"
              v-model.number="sectionData.content.accuracyWeight"
              class="p-input"
              min="0"
              max="100"
              placeholder="정확성 비중"
            />
          </div>
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round pg-mem-notes__field-ico">bolt</i>
              Speed Weight (%)
            </label>
            <input
              type="number"
              v-model.number="sectionData.content.speedWeight"
              class="p-input"
              min="0"
              max="100"
              placeholder="속도 비중"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineModel, watch } from 'vue';
import ABtn from "@/components/Pineapple/ABtn.vue";

const sectionData = defineModel({ type: Object, required: true });

// 데이터 구조 초기화
watch(() => sectionData.value.content, (newContent) => {
  if (!newContent.chunks) {
    sectionData.value.content.chunks = [];
  }
  if (!newContent.accuracyWeight) {
    sectionData.value.content.accuracyWeight = 70;
  }
  if (!newContent.speedWeight) {
    sectionData.value.content.speedWeight = 30;
  }
}, { immediate: true, deep: true });

const addChunk = () => {
  sectionData.value.content.chunks.push({
    sourceContent: '',
    keySymbols: '',
    expectedNotes: '',
    timeLimit: 120,
    difficulty: 'intermediate',
    speechRate: 'normal'
  });
};

const removeChunk = (index) => {
  sectionData.value.content.chunks.splice(index, 1);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-mem-notes {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__header {
    padding-bottom: 12px;
    border-bottom: 1px solid $p-line;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    > i {
      font-size: 22px;
      color: $p-point-deep;
    }
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__section-label {
    font-size: 14px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px dashed $p-line;

    > i {
      font-size: 18px;
      color: $p-ink-mid;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    min-width: 28px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 800;
    display: inline-grid;
    place-items: center;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__field-ico {
    font-size: 14px !important;
    color: $p-ink-mid;
    vertical-align: middle;
    margin-right: 4px;
  }

  &__settings {
    padding: 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
  }

  &__assessment {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>
